/**
 * Admin Layout - Administration Navigation Wrapper
 * 
 * Provides the sidebar navigation for all admin pages.
 */

import React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  LayoutDashboard, Users, Building2, Package, CreditCard,
  Briefcase, Shield, Database, FileText, ChevronLeft, Settings,
  BarChart3, Bell
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { BRANDING } from '../../config/branding';

const navItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/admin/customers', icon: Users, label: 'Clients SaaS' },
  { path: '/admin/riads', icon: Building2, label: 'Riads' },
  { path: '/admin/subscriptions', icon: CreditCard, label: 'Abonnements' },
  { path: '/admin/billing', icon: FileText, label: 'Facturation' },
  { path: '/admin/billing-dashboard', icon: BarChart3, label: 'Analytics Billing' },
  { path: '/admin/plans', icon: Package, label: 'Plans' },
  { path: '/admin/concierges', icon: Briefcase, label: 'Conciergeries' },
  { path: '/admin/users', icon: Shield, label: 'Super Admins' },
  { path: '/admin/access', icon: Users, label: 'Acces' },
  { path: '/admin/backups', icon: Database, label: 'Backups' },
  { path: '/admin/audit', icon: FileText, label: 'Audit' },
  { path: '/admin/notifications', icon: Bell, label: 'Notifications' },
  { path: '/admin/config', icon: Settings, label: 'Config Tenants' },
  { path: '/admin/settings', icon: Settings, label: 'Parametres' },
];

const AdminLayout = () => {
  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-700/50 bg-gradient-to-r from-slate-900 to-slate-800">
          <div className="flex items-center gap-3">
            <img 
              src={BRANDING.logo} 
              alt={BRANDING.name} 
              className="w-10 h-10 drop-shadow-lg" 
              data-testid="admin-sidebar-logo"
            />
            <div>
              <h1 className="font-bold text-base text-white">{BRANDING.name}</h1>
              <p className="text-xs text-slate-400 tracking-wide uppercase">Super Admin</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive
                    ? 'bg-violet-600 text-white font-medium'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-slate-700">
          <NavLink to="/">
            <Button variant="ghost" className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800">
              <ChevronLeft className="w-4 h-4 mr-2" />
              Retour à l'app
            </Button>
          </NavLink>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
